import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Form, Alert, Spinner, Row, Col } from 'react-bootstrap';
import { getAuthHeaders } from '../utils/authHelpers';
import 'bootstrap/dist/css/bootstrap.min.css';

const API_ROOT_URL = process.env.REACT_APP_API_ROOT_URL;

const COLORS = ['Burgundy', 'Hemp Beige', 'Onyx Black', 'Royal Blue', 'Forest Green', 'Sea Gull Grey', 'Buckskin Tan', 'Aquamarine', 'Caribbean Blue', 'Chocolate Brown', 'Teal', 'Turquoise', 'Harbor Blue', 'Navy Blue', 'Charcoal', 'Arctic White', 'Birch', 'Sand', 'Taupe', 'Sunset Red', 'Vision - Onyx Black', 'Vision - Sea Gull Gray', 'Vision - Hemp Beige', 'Vision - Arctic White', 'Black Mesh', 'N/A'];
const FRAME_TYPES = ['Pontoon', 'Runabout', 'PWC', 'Golf Cart'];
const SHIPPING_METHODS = ['Postal Service', 'LTL', 'Piggyback', 'Pickup'];

function EditOrderModal({ show, handleClose, orderToEdit, setSubmissionMessage }) {
  const [formData, setFormData] = useState({
    orderType: '',
    shippingMethod: '',
    dealerId: '',
    dealerName: '',
    customerName: '',
    customWork: '',
    frameType: '',
    length: '',
    width: '',
    height: '',
    upperColor: '',
    lowerColor: '',
    brackets: '',
    revenue: '',
    orderDate: '',
    paymentDate: '',
    invoiceNumber: '',
    tbcNumber: '',
    shippedDate: ''
  });

  // Piggyback UI state
  const [piggybackOption, setPiggybackOption] = useState('next'); // 'next' | 'custom'
  const [piggybackOrderName, setPiggybackOrderName] = useState('');

  // Inline validation state
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});

  // Local-only modal alert
  const [modalMessage, setModalMessage] = useState('');
  const [modalMessageType, setModalMessageType] = useState('danger');

  // Dealers
  const [dealers, setDealers] = useState([]);
  const [loadingDealers, setLoadingDealers] = useState(false);

  const [loading, setLoading] = useState(false);

  const isCompletedOrder = useMemo(
    () => orderToEdit?.currentStage === 'Completed',
    [orderToEdit]
  );

  const isCompletedPickup = useMemo(
  () => orderToEdit?.currentStage === 'Completed' && orderToEdit?.shippingMethod.includes('Pickup'),
  [orderToEdit]
);

  const isParts = formData.orderType === 'Parts';

  // Parse an existing shippingMethod like "Piggyback with Next Cover" or "Piggyback with XYZ"
  const parsePiggyback = (shippingMethod) => {
    if (!shippingMethod) return { base: '', option: 'next', name: '' };
    if (shippingMethod.startsWith('Piggyback with')) {
      const rest = shippingMethod.replace('Piggyback with', '').trim(); // "Next Cover" or "XYZ"
      if (rest.toLowerCase() === 'next cover') {
        return { base: 'Piggyback', option: 'next', name: '' };
      }
      const name = rest.replace(/^with\s+/i, '').trim();
      return { base: 'Piggyback', option: 'custom', name };
    }
    return { base: shippingMethod, option: 'next', name: '' };
  };

  // Populate form from orderToEdit
  useEffect(() => {
    if (!orderToEdit) return;

    const {
      orderType,
      shippingMethod,
      dealerId,
      dealerName,
      customerName,
      customWork,
      frameType,
      length,
      width,
      height,
      upperColor,
      lowerColor,
      brackets,
      revenue,
      orderDate,
      invoiceNumber,
      shipping
    } = orderToEdit;

    const { base, option, name } = parsePiggyback(shippingMethod);

    setFormData({
      orderType: orderType || '',
      shippingMethod: base || '',
      dealerId: dealerId || '',
      dealerName: dealerName || '',
      customerName: customerName || '',
      customWork: customWork ?? '',
      frameType: frameType || '',
      length: length ?? '',
      width: width ?? '',
      height: height ?? '',
      upperColor: upperColor || '',
      lowerColor: lowerColor || '',
      brackets: brackets ?? '',
      revenue: revenue ?? '',
      orderDate: orderDate ? orderDate.split('T')[0] : '',
      paymentDate: orderToEdit.administration?.paymentCompletedDate
      ? orderToEdit.administration.paymentCompletedDate.split('T')[0]
      : '',
      invoiceNumber: invoiceNumber ?? '',
      tbcNumber: orderToEdit.tbcNumber ?? '',
      shippedDate: shipping?.shippedDate ? shipping.shippedDate.split('T')[0] : ''
    });

    setPiggybackOption(option);
    setPiggybackOrderName(name);
    setModalMessage('');
    setModalMessageType('danger');
    setSubmissionMessage('', '');
    setTouched({});
    setErrors({});
  }, [orderToEdit, setSubmissionMessage]);

  useEffect(() => {
    const fetchDealers = async () => {
      setLoadingDealers(true);
      try {
        const response = await fetch(`${API_ROOT_URL}/dealers`, {
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
        });
        if (response.ok) {
          const data = await response.json();
          setDealers(
            data.sort((a, b) => a.dealerName.localeCompare(b.dealerName))
          );
        } else {
          console.error('Failed to load dealers list');
        }
      } catch (err) {
        console.error('Error fetching dealers:', err);
      } finally {
        setLoadingDealers(false);
      }
    };

    fetchDealers();
  }, []);

  const onHide = () => {
    setFormData({
      orderType: '',
      shippingMethod: '',
      dealerId: '',
      dealerName: '',
      customerName: '',
      customWork: '',
      frameType: '',
      length: '',
      width: '',
      height: '',
      upperColor: '',
      lowerColor: '',
      brackets: '',
      revenue: '',
      orderDate: '',
      paymentDate: '',
      invoiceNumber: '',
      tbcNumber: '',
      shippedDate: ''
    });
    setPiggybackOption('next');
    setPiggybackOrderName('');
    setModalMessage('');
    setModalMessageType('danger');
    setTouched({});
    setErrors({});
    handleClose();
  };

  const setFieldTouched = (name) => {
    setTouched(prev => ({ ...prev, [name]: true }));
  };

  // Build validation errors
  const buildErrors = (data, pigOpt, pigName) => {
    const e = {};

    // Base required
    if (!data.shippingMethod) e.shippingMethod = 'Shipping Method is required.';
    if (!data.dealerName || !data.dealerName.trim()) e.dealerName = 'Dealer Name is required.';
    if (!data.customerName || !data.customerName.trim()) e.customerName = 'Customer Name is required.';

    if (data.invoiceNumber && !data.invoiceNumber.trim()) {
      e.invoiceNumber = 'Invoice number cannot be just whitespace.';
    }

    if (data.tbcNumber && !data.tbcNumber.trim()) {
      e.tbcNumber = 'TBC number cannot be just whitespace.';
    }

    // Piggyback rules
    if (data.shippingMethod === 'Piggyback') {
      if (pigOpt === 'custom') {
        if (!pigName || !pigName.trim()) {
          e.piggybackOrderName = 'Enter the specific order name for piggyback.';
        } else if (pigName.trim().length < 2) {
          e.piggybackOrderName = 'Piggyback order name must be at least 2 characters.';
        }
      }
    }

    // Non-Parts requireds
    if (!isParts) {
      if (!data.frameType) e.frameType = 'Frame Type is required.';
      if (data.length === '' && data.length !== 0) e.length = 'Length is required.';
      if (data.width === '' && data.width !== 0) e.width = 'Width is required.';
      if (data.height === '' && data.height !== 0) e.height = 'Height is required.';
      if (!data.upperColor) e.upperColor = 'Upper Color is required.';
      if (!data.lowerColor) e.lowerColor = 'Lower Color is required.';
      if (data.revenue === '' && data.revenue !== 0) e.revenue = 'Revenue is required.';

      // Numeric (positive)
      const toNum = (v) => (v === '' ? NaN : Number(v));
      const len = toNum(data.length);
      const wid = toNum(data.width);
      const hei = toNum(data.height);
      const rev = toNum(data.revenue);

      if (data.length !== '' && (Number.isNaN(len) || len < 0)) e.length = 'Length must be a positive number.';
      if (data.width !== '' && (Number.isNaN(wid) || wid < 0)) e.width = 'Width must be a positive number.';
      if (data.height !== '' && (Number.isNaN(hei) || hei < 0)) e.height = 'Height must be a positive number.';
      if (data.revenue !== '' && (Number.isNaN(rev) || rev < 0)) e.revenue = 'Revenue must be a positive amount.';
    } else {
      // Parts: revenue optional; if provided must be non-negative
      if (data.revenue !== '' && (Number.isNaN(Number(data.revenue)) || Number(data.revenue) < 0)) {
        e.revenue = 'Revenue must be a non-negative amount.';
      }
    }

    // Order date optional but must be valid if provided
    if (data.orderDate) {
      const d = new Date(data.orderDate);
      if (Number.isNaN(d.getTime())) e.orderDate = 'Invalid date.';
    }

    if (data.paymentDate) {
      const d = new Date(data.paymentDate);
      if (Number.isNaN(d.getTime())) e.paymentDate = 'Invalid date.';
    }

    return e;
  };

  const validateAndSet = (next = formData, nextPigOpt = piggybackOption, nextPigName = piggybackOrderName) => {
    const e = buildErrors(next, nextPigOpt, nextPigName);
    setErrors(e);
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFieldTouched(name);

    setFormData(prev => {
      const next = { ...prev, [name]: value };
      // Reset piggyback specifics if switching away
      if (name === 'shippingMethod' && value !== 'Piggyback') {
        setPiggybackOption('next');
        setPiggybackOrderName('');
        validateAndSet(next, 'next', '');
        return next;
      }
      validateAndSet(next, piggybackOption, piggybackOrderName);
      return next;
    });
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setFieldTouched(name);
    validateAndSet();
  };

  const handlePiggybackOptionChange = (opt) => {
    setPiggybackOption(opt);
    setFieldTouched('shippingMethod');
    validateAndSet(formData, opt, piggybackOrderName);
  };

  const handlePiggybackNameChange = (val) => {
    setPiggybackOrderName(val);
    setFieldTouched('piggybackOrderName');
    validateAndSet(formData, piggybackOption, val);
  };

  const buildFinalShippingMethod = () => {
    if (formData.shippingMethod !== 'Piggyback') {
      return formData.shippingMethod || '';
    }
    if (piggybackOption === 'next') {
      return 'Piggyback with Next Cover';
    }
    const trimmed = piggybackOrderName.trim();
    if (!trimmed) return ''; // will be validated before submit
    return `Piggyback with ${trimmed}`;
  };

  const checkDuplicateInvoice = async (invoiceNumber, orderId) => {
    try {
      const authHeaders = getAuthHeaders();
      const response = await fetch(`${API_ROOT_URL}/orders?invoiceNumber=${encodeURIComponent(invoiceNumber)}`, {
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        }
      });
      if (!response.ok) {
        throw new Error('Failed to fetch orders for duplicate check.');
      }
      const existingOrders = await response.json();
      // Check for an existing order with the same invoice number but a different ID
      const duplicateFound = existingOrders.some(order => order.invoiceNumber === invoiceNumber && order.orderId !== orderId);
      return duplicateFound;
    } catch (err) {
      console.error('Error during duplicate invoice check:', err);
      return false; // Return false to not block submission on a network error
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!orderToEdit?.orderId) {
      setModalMessage('Error: Order ID missing for update.');
      setModalMessageType('danger');
      return;
    }

    if (isCompletedOrder && !isCompletedPickup) {
  // Allow editing only the payment date for completed orders
  const onlyPaymentDateChanged =
    formData.paymentDate !==
    (orderToEdit.administration?.paymentCompletedDate
      ? orderToEdit.administration.paymentCompletedDate.split('T')[0]
      : '');

  if (!onlyPaymentDateChanged) {
    setModalMessage('This order is completed and only the payment date can be edited.');
    setModalMessageType('info');
    return;
  }
}


    setLoading(true);
    setModalMessage('');

    // Mark fields as touched
    const touchAll = {
      shippingMethod: true,
      dealerName: true,
      customerName: true,
      orderDate: !!formData.orderDate,
      invoiceNumber: !!formData.invoiceNumber,
      tbcNumber: !!formData.tbcNumber,
    };
    if (formData.shippingMethod === 'Piggyback' && piggybackOption === 'custom') {
      touchAll.piggybackOrderName = true;
    }
    if (!isParts) {
      Object.assign(touchAll, {
        frameType: true,
        length: true,
        width: true,
        height: true,
        upperColor: true,
        lowerColor: true,
        revenue: true
      });
    } else if (formData.revenue !== '') {
      touchAll.revenue = true;
    }
    setTouched(prev => ({ ...prev, ...touchAll }));

    // Validate
    const eObj = validateAndSet(formData, piggybackOption, piggybackOrderName);
    if (Object.keys(eObj).length > 0) {
      setModalMessage('Please correct the highlighted fields.');
      setModalMessageType('danger');
      setLoading(false);
      return;
    }

    // Duplicate invoice number check
    const newInvoiceNumber = formData.invoiceNumber?.trim();
    const oldInvoiceNumber = orderToEdit.invoiceNumber?.trim();

    if (newInvoiceNumber && newInvoiceNumber !== oldInvoiceNumber) {
      const isDuplicate = await checkDuplicateInvoice(newInvoiceNumber, orderToEdit.orderId);
      if (isDuplicate) {
        setErrors(prev => ({ ...prev, invoiceNumber: 'This invoice number already exists for another order.' }));
        setModalMessage('This invoice number already exists for another order.');
        setModalMessageType('danger');
        setLoading(false);
        return;
      }
    }

    // Build final payload
    const finalShip = buildFinalShippingMethod();
    const dataToSend = {
      customerName: formData.customerName,
      customWork: formData.customWork || '',
      shippingMethod: finalShip,
      dealerId: formData.dealerId || null,
      dealerName: formData.dealerName || null,
      orderDate: formData.orderDate
        ? new Date(`${formData.orderDate}T00:00:00Z`).toISOString()
        : null,
      administration: {
        paymentCompletedDate: formData.paymentDate
          ? new Date(`${formData.paymentDate}T00:00:00Z`).toISOString()
          : null
      },
      invoiceNumber: formData.invoiceNumber || null,
      tbcNumber: formData.tbcNumber || null,
      frameType: !isParts ? formData.frameType : undefined,
      length: !isParts ? Number(formData.length) : undefined,
      width: !isParts ? Number(formData.width) : undefined,
      height: !isParts ? Number(formData.height) : undefined,
      upperColor: !isParts ? formData.upperColor : undefined,
      lowerColor: !isParts ? formData.lowerColor : undefined,
      brackets: !isParts ? (formData.brackets ?? '') : undefined,
      revenue: formData.revenue === '' ? undefined : Number(formData.revenue)
    };

    if (isCompletedPickup) {
      dataToSend.shipping = {
        shippedDate: formData.shippedDate
          ? new Date(`${formData.shippedDate}T00:00:00Z`).toISOString()
          : null
      };
    }

    // Remove product fields if Parts order
    if (isParts) {
      delete dataToSend.frameType;
      delete dataToSend.length;
      delete dataToSend.width;
      delete dataToSend.height;
      delete dataToSend.upperColor;
      delete dataToSend.lowerColor;
      delete dataToSend.brackets;
    }

    try {
      const response = await fetch(`${API_ROOT_URL}/orders/${orderToEdit.orderId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(dataToSend)
      });

      const result = await response.json();

      if (response.ok) {
        setSubmissionMessage(result.message || 'Order updated successfully!', 'success');
        handleClose(true);
      } else {
        setModalMessage(result.message || 'Failed to update order.');
        setModalMessageType('danger');
      }
    } catch (err) {
      console.error('Error updating order:', err);
      setModalMessage('Network error or server is unreachable while updating order.');
      setModalMessageType('danger');
    } finally {
      setLoading(false);
    }
  };

  // Helpers for inline error state
  const invalid = (name) => !!(touched[name] && errors[name]);
  const feedback = (name) => (touched[name] && errors[name]) ? errors[name] : '';

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Edit Order: {orderToEdit?.dealerName || ''} - {orderToEdit?.customerName || ''}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isCompletedOrder && (
          <Alert variant="info" className="mb-3">
            This order has been marked as "Completed" and cannot be edited.
          </Alert>
        )}

        {modalMessage && <Alert variant={modalMessageType}>{modalMessage}</Alert>}

        <Form onSubmit={handleSubmit} noValidate>
          {/* Order Type (read-only, gray) */}
          <Form.Group className="mb-3">
            <Form.Label>Order Type</Form.Label>
            <Form.Control type="text" value={formData.orderType} readOnly style={{ backgroundColor: '#e9ecef' }} />
          </Form.Group>

          {/* Shipping */}
          <Form.Group className="mb-3">
            <Form.Label>Shipping Method</Form.Label>
            <Form.Select
              name="shippingMethod"
              value={formData.shippingMethod}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={invalid('shippingMethod')}
              disabled={loading || isCompletedOrder}
            >
              <option value="" disabled>Select Shipping Method</option>
              {SHIPPING_METHODS.map(method => (
                <option key={method} value={method}>{method}</option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {feedback('shippingMethod')}
            </Form.Control.Feedback>
          </Form.Group>

          {formData.shippingMethod === 'Piggyback' && (
            <>
              <Form.Group className="mb-3">
                <Form.Check
                  type="radio"
                  label="Piggyback with Next Cover"
                  checked={piggybackOption === 'next'}
                  onChange={() => handlePiggybackOptionChange('next')}
                  disabled={loading || isCompletedOrder}
                />
                <Form.Check
                  type="radio"
                  label="Piggyback with Specific Order"
                  checked={piggybackOption === 'custom'}
                  onChange={() => handlePiggybackOptionChange('custom')}
                  disabled={loading || isCompletedOrder}
                />
              </Form.Group>
              {piggybackOption === 'custom' && (
                <Form.Group className="mb-3">
                  <Form.Control
                    type="text"
                    placeholder="Enter Order Name"
                    value={piggybackOrderName}
                    onChange={(e) => handlePiggybackNameChange(e.target.value)}
                    onBlur={() => setFieldTouched('piggybackOrderName')}
                    isInvalid={invalid('piggybackOrderName')}
                    disabled={loading || isCompletedOrder}
                  />
                  <Form.Control.Feedback type="invalid">
                    {feedback('piggybackOrderName')}
                  </Form.Control.Feedback>
                </Form.Group>
              )}
            </>
          )}

          {/* Invoice Number */}
          <Form.Group className="mb-3">
            <Form.Label>Invoice Number (Optional)</Form.Label>
            <Form.Control
              type="text"
              name="invoiceNumber"
              value={formData.invoiceNumber}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={invalid('invoiceNumber')}
              placeholder="Enter invoice number"
              disabled={loading || isCompletedOrder}
            />
            <Form.Control.Feedback type="invalid">
              {feedback('invoiceNumber')}
            </Form.Control.Feedback>
          </Form.Group>

          {/* TBC Number */}
          <Form.Group className="mb-3">
            <Form.Label>TBC Number (Optional)</Form.Label>
            <Form.Control
              type="text"
              name="tbcNumber"
              value={formData.tbcNumber}
              onChange={(e) => handleChange({ target: { name: 'tbcNumber', value: e.target.value.toUpperCase() } })}
              onBlur={handleBlur}
              isInvalid={invalid('tbcNumber')}
              placeholder="Enter TBC number"
              disabled={loading || isCompletedOrder}
            />
            <Form.Control.Feedback type="invalid">
              {feedback('tbcNumber')}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Dealer (editable dropdown) */}
          <Form.Group className="mb-3">
            <Form.Label>Dealer</Form.Label>
            <Form.Select
              name="dealerId"
              value={formData.dealerId}
              onChange={(e) => {
                const selected = dealers.find(d => d.dealerId === e.target.value);
                setFormData(prev => ({
                  ...prev,
                  dealerId: selected?.dealerId || '',
                  dealerName: selected?.dealerName || '',
                }));
                setFieldTouched('dealerName');
                validateAndSet(
                  { ...formData, dealerId: selected?.dealerId || '', dealerName: selected?.dealerName || '' },
                  piggybackOption,
                  piggybackOrderName
                );
              }}
              onBlur={handleBlur}
              isInvalid={invalid('dealerName')}
              disabled={loading || loadingDealers || isCompletedOrder}
            >
              <option value="">Select Dealer</option>
              {dealers.map((d) => (
                <option key={d.dealerId} value={d.dealerId}>
                  {d.dealerName}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {feedback('dealerName')}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Customer */}
          <Form.Group className="mb-3">
            <Form.Label>Customer Name</Form.Label>
            <Form.Control
              name="customerName"
              value={formData.customerName}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={invalid('customerName')}
              disabled={loading || isCompletedOrder}
              placeholder="Enter customer's name"
            />
            <Form.Control.Feedback type="invalid">
              {feedback('customerName')}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Non-Parts specs */}
          {formData.orderType !== 'Parts' && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Frame Type</Form.Label>
                <Form.Select
                  name="frameType"
                  value={formData.frameType}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={!isParts && invalid('frameType')}
                  disabled={loading || isCompletedOrder}
                >
                  <option value="">Select Frame Type</option>
                  {FRAME_TYPES.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {feedback('frameType')}
                </Form.Control.Feedback>
              </Form.Group>

              <Row className="mb-3">
                <Col>
                  <Form.Control
                    type="number"
                    placeholder="Length"
                    name="length"
                    value={formData.length}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={!isParts && invalid('length')}
                    disabled={loading || isCompletedOrder}
                    min="0"
                    step="0.01"
                  />
                  <Form.Control.Feedback type="invalid">
                    {feedback('length')}
                  </Form.Control.Feedback>
                </Col>
                <Col>
                  <Form.Control
                    type="number"
                    placeholder="Width"
                    name="width"
                    value={formData.width}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={!isParts && invalid('width')}
                    disabled={loading || isCompletedOrder}
                    min="0"
                    step="0.01"
                  />
                  <Form.Control.Feedback type="invalid">
                    {feedback('width')}
                  </Form.Control.Feedback>
                </Col>
                <Col>
                  <Form.Control
                    type="number"
                    placeholder="Height"
                    name="height"
                    value={formData.height}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={!isParts && invalid('height')}
                    disabled={loading || isCompletedOrder}
                    min="0"
                    step="0.01"
                  />
                  <Form.Control.Feedback type="invalid">
                    {feedback('height')}
                  </Form.Control.Feedback>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col>
                  <Form.Select
                    name="upperColor"
                    value={formData.upperColor}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={!isParts && invalid('upperColor')}
                    disabled={loading || isCompletedOrder}
                  >
                    <option value="">Select Upper Color</option>
                    {COLORS.map(color => <option key={color} value={color}>{color}</option>)}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {feedback('upperColor')}
                  </Form.Control.Feedback>
                </Col>
                <Col>
                  <Form.Select
                    name="lowerColor"
                    value={formData.lowerColor}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={!isParts && invalid('lowerColor')}
                    disabled={loading || isCompletedOrder}
                  >
                    <option value="">Select Lower Color</option>
                    {COLORS.map(color => <option key={color} value={color}>{color}</option>)}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {feedback('lowerColor')}
                  </Form.Control.Feedback>
                </Col>
              </Row>
            </>
          )}

          {/* Custom Work (ALL orders including Parts) */}
          <Form.Group className="mb-3">
            <Form.Label>Custom Work</Form.Label>
            <Form.Control
              as="textarea"
              placeholder="Describe custom work"
              name="customWork"
              value={formData.customWork}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={loading || isCompletedOrder}
            />
          </Form.Group>

          {/* Brackets (non-Parts only) */}
          {formData.orderType !== 'Parts' && (
            <Form.Group className="mb-3">
              <Form.Label>Brackets</Form.Label>
              <Form.Control
                type="text"
                name="brackets"
                value={formData.brackets}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={loading || isCompletedOrder}
                placeholder="e.g., Type A, custom brackets note"
              />
            </Form.Group>
          )}

          {/* Revenue (ALL orders; optional for Parts) */}
          <Form.Group className="mb-3">
            <Form.Label>Revenue</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              name="revenue"
              value={formData.revenue}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={(!isParts && invalid('revenue')) || (isParts && touched.revenue && !!errors.revenue)}
              disabled={loading || isCompletedOrder}
            />
            <Form.Control.Feedback type="invalid">
              {!isParts ? feedback('revenue') : (touched.revenue && errors.revenue ? errors.revenue : '')}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Order Date (Optional) */}
          <Form.Group className="mb-3">
            <Form.Label>Order Date</Form.Label>
            <Form.Control
              type="date"
              name="orderDate"
              value={formData.orderDate}
              onChange={handleChange}
              onBlur={handleBlur}
              className="w-auto"
              isInvalid={invalid('orderDate')}
              disabled={loading || isCompletedOrder}
            />
            <Form.Control.Feedback type="invalid">
              {feedback('orderDate')}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Payment Date (Always editable) */}
          <Form.Group className="mb-3">
            <Form.Label>Payment Date</Form.Label>
            <Form.Control
              type="date"
              name="paymentDate"
              value={formData.paymentDate}
              onChange={handleChange}
              onBlur={handleBlur}
              className="w-auto"
              isInvalid={invalid('paymentDate')}
              disabled={loading} // always editable, even after completion
            />
            <Form.Control.Feedback type="invalid">
              {feedback('paymentDate')}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Shipping Date (Editable for completed pickup orders) */}
          {isCompletedPickup && (
            <Form.Group className="mb-3">
              <Form.Label>Shipping/Pickup Date</Form.Label>
              <Form.Control
                type="date"
                name="shippedDate"
                value={formData.shippedDate}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={invalid('shippedDate')}
                className="w-auto"
                // Disable the field only when the form is loading
                disabled={loading}
              />
              <Form.Control.Feedback type="invalid">
                {feedback('shippedDate')}
              </Form.Control.Feedback>
            </Form.Group>
          )}

          <Button
            variant="primary"
            type="submit"
            disabled={loading}
            className="w-100"
          >
            {loading ? <Spinner animation="border" size="sm" /> : 'Update Order'}
          </Button>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

export default EditOrderModal;
